import { readAndCompressImage } from 'browser-image-resizer';
import heic2any from 'heic2any';

/**
 * Allowed image MIME types for upload validation.
 * Add or remove entries here to control which image formats are accepted across the app.
 */
const ALLOWED_IMAGE_MIME_TYPES: string[] = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/heic',
];

/**
 * Allowed image file extensions (used for <input accept=""> attribute).
 * Must be kept in sync with ALLOWED_IMAGE_MIME_TYPES above.
 */
const ALLOWED_IMAGE_EXTENSIONS: string[] = [
    '.png',
    '.jpg',
    '.jpeg',
    '.webp',
    '.heic',
];

/**
 * Maximum file size in bytes (default 10MB).
 * Change this value to adjust the upload size limit across the app.
 */
const MAX_IMAGE_FILE_SIZE: number = 10 * 1024 * 1024;

export interface ImageValidationResult {
    valid: boolean;
    errorMessage: string;
}

export class ImageUtil {

    /**
     * Returns the list of allowed MIME types.
     * Useful for programmatic checks outside of this util.
     */
    public static getAllowedMimeTypes(): string[] {
        return [...ALLOWED_IMAGE_MIME_TYPES];
    }

    /**
     * Returns a comma-separated string of allowed extensions
     * suitable for use in <input type="file" accept="..."> attributes.
     *
     * Example: ".png,.jpg,.jpeg,.gif,.webp,.bmp"
     */
    public static getAcceptAttribute(): string {
        return ALLOWED_IMAGE_EXTENSIONS.join(',');
    }

    /**
     * Returns a human-readable string of allowed formats for error messages.
     *
     * Example: "PNG, JPG, JPEG, GIF, WEBP, BMP"
     */
    public static getAllowedFormatsLabel(): string {
        return ALLOWED_IMAGE_EXTENSIONS
            .map(ext => ext.replace('.', '').toUpperCase())
            .join(', ');
    }

    /**
     * Validates whether a given file is an allowed image format and within size limits.
     *
     * @param file - The File object to validate
     * @param maxSizeBytes - Optional max file size override (defaults to MAX_IMAGE_FILE_SIZE)
     * @returns ImageValidationResult with valid flag and error message
     *
     * Usage:
     *   const result = ImageUtil.validateImageFile(file);
     *   if (!result.valid) {
     *       alert(result.errorMessage);
     *       return;
     *   }
     */
    public static validateImageFile(file: File, maxSizeBytes: number = MAX_IMAGE_FILE_SIZE): ImageValidationResult {
        if (!file) {
            return { valid: false, errorMessage: 'ファイルが選択されていません。' };
        }

        if (file.size === 0) {
            return { valid: false, errorMessage: 'ファイルが空です。' };
        }

        // Check MIME type
        const mimeType = file.type.toLowerCase();
        if (!ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) {
            // Fallback: also check by file extension in case MIME type is empty/incorrect
            const fileName = file.name.toLowerCase();
            const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some(ext => fileName.endsWith(ext));
            if (!hasValidExtension) {
                return {
                    valid: false,
                    errorMessage: `この画像形式はサポートされていません。\n対応形式: ${ImageUtil.getAllowedFormatsLabel()}`
                };
            }
        }

        // Check file size
        if (file.size > maxSizeBytes) {
            const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
            return {
                valid: false,
                errorMessage: `ファイルサイズが大きすぎます。${maxSizeMB}MB以下のファイルを選択してください。`
            };
        }

        return { valid: true, errorMessage: '' };
    }

    /**
     * Validates multiple files at once.
     * Returns the first validation failure, or a valid result if all pass.
     *
     * @param files - FileList or array of File objects
     * @param maxSizeBytes - Optional max file size override
     */
    public static validateImageFiles(files: FileList | File[], maxSizeBytes: number = MAX_IMAGE_FILE_SIZE): ImageValidationResult {
        const fileArray = files instanceof FileList ? Array.from(files) : files;

        for (const file of fileArray) {
            const result = ImageUtil.validateImageFile(file, maxSizeBytes);
            if (!result.valid) {
                return result;
            }
        }

        return { valid: true, errorMessage: '' };
    }

    /**
     * Returns true if the file is one of the allowed image types (MIME or extension).
     * Does NOT match arbitrary image/* MIME types — only the ALLOWED list.
     */
    public static isImageFile(file: File): boolean {
        const mimeType = file.type.toLowerCase();
        if (ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) {
            return true;
        }
        const fileName = file.name.toLowerCase();
        return ALLOWED_IMAGE_EXTENSIONS.some(function(ext) {
            return fileName.length >= ext.length && fileName.indexOf(ext) === fileName.length - ext.length;
        });
    }

    /**
     * For mixed-file upload contexts: validates only if the file is an image.
     * Non-image files pass through without validation.
     */
    public static validateFileIfImage(file: File, maxSizeBytes: number = MAX_IMAGE_FILE_SIZE): ImageValidationResult {
        if (!file) {
            return { valid: false, errorMessage: 'ファイルが選択されていません。' };
        }
        if (ImageUtil.isImageFile(file)) {
            return ImageUtil.validateImageFile(file, maxSizeBytes);
        }
        return { valid: true, errorMessage: '' };
    }

    private static isHeicFile(file: File): boolean {
        var mime = file.type.toLowerCase();
        if (mime === 'image/heic' || mime === 'image/heif') {
            return true;
        }
        var name = file.name.toLowerCase();
        return name.indexOf('.heic') === name.length - 5 || name.indexOf('.heif') === name.length - 5;
    }

    /**
     * Converts HEIC/HEIF to JPEG blob.
     */
    private static convertHeicToJpeg(file: File): Promise<Blob> {
        return heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 }).then(function(result) {
            if (Array.isArray(result)) {
                return result[0];
            }
            return result;
        });
    }

    /**
     * Compresses an image file. Converts HEIC to JPEG first if needed.
     * For non-compressible formats, returns the original file as-is.
     */
    public static compress(file: File, maxWidth = 2048) {
        var resizeConfig = {
            quality: 1.0,
            maxWidth: maxWidth,
            maxHeight: maxWidth,
            autoRotate: false,
            debug: true
        };

        if (file && ImageUtil.isHeicFile(file)) {
            return ImageUtil.convertHeicToJpeg(file).then(function(jpegBlob) {
                return readAndCompressImage(jpegBlob as any, resizeConfig);
            });
        }

        if (file && (file.type == "image/png" || file.type == "image/jpg" || file.type == "image/jpeg" || file.type == "image/webp")) {
            return readAndCompressImage(file, resizeConfig);
        }

        return new Promise(function(resolve) { resolve(file); });
    }
}
